import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import Masonry from 'react-masonry-css'

import { Breadcrumbs } from '@/components/atomic/Breadcrumbs'
import { Loader } from '@/components/atomic/Loader'
import { PageContainer } from '@/components/atomic/PageContainer'
import { PageMetaData } from '@/components/atomic/PageMetaData'
import { Pagination } from '@/components/atomic/Pagination'
import { ButtonHierarchy } from '@/components/styleguide/Button'
import { ChatWidget } from '@/components/styleguide/ChatWidget'
import { Header } from '@/components/styleguide/Header'
import { InternalLink } from '@/components/styleguide/Link'
import { constants } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { getPageContentListService } from '@/lib/services/pageService'
import { Size } from '@/types/system'

const breakpointColumns = {
  default: 4,
  1200: 3,
  992: 2,
  576: 1,
}

const BlogPage = ({ articles }) => {
  const router = useRouter()
  const [status, setStatus] = useState(constants.componentStatus.LOADING)
  const [blogList, setBlogList] = useState(articles.items)
  const [currentPage, setCurrentPage] = useState(router.query.page ? router.query.page : 1)
  const [totalCount, setTotalCount] = useState(articles.totalCount)

  useEffect(() => {
    const requestParams = {
      type: 'BLOG',
      page: currentPage.toString(),
      pageSize: 12,
    }

    async function fetchData(requestParams) {
      try {
        const response = await getPageContentListService(requestParams)

        setBlogList(response.items)
        setTotalCount(response.totalCount)
        return setStatus(constants.componentStatus.COMPLETE)
      } catch (err) {
        if (err.status === 404) {
          return setStatus(constants.componentStatus.NOT_FOUND)
        } else {
          return setStatus(constants.componentStatus.ERROR)
        }
      }
    }

    fetchData(requestParams)
  }, [currentPage])

  const onClickMore = page => {
    setCurrentPage(page)
  }

  return (
    <>
      <PageMetaData
        title="LeaseLoco Blog: Car Leasing News, Industry Updates &amp; More"
        description="Take a pit stop, grab all the latest car leasing news, industry insight and more on the LeaseLoco blog. The UK's leading car leasing comparison site."
        pageUrl={ROUTES.blogPage.url}
      />
      <ChatWidget />
      <Header
        backgroundColor="white"
        backButtonUrl={ROUTES.homePage.url}
      />
      <PageContainer containerClass="container--blog">
        <Breadcrumbs
          breadcrumbs={[
            {
              title: 'Home',
              slug: ROUTES.homePage.url,
            },
            {
              title: `Leasing Blog`,
              slug: null,
            },
          ]}
        />
        <h1>Leasing Blog</h1>
        <p>
          Welcome to the LeaseLoco blog Amigos! We&apos;re the biggest{' '}
          <Link href={ROUTES.homePage.url}> car leasing comparison site</Link> in the UK, with
          millions of lease deals available. Here on the blog we add extra insight from our leasing
          experts as well as the latest car industry news and updates.
        </p>
        {status === constants.componentStatus.LOADING ? (
          <div className="d-flex justify-content-center">
            <Loader />
          </div>
        ) : (
          <Masonry
            breakpointCols={breakpointColumns}
            className="masonry-grid"
            columnClassName="masonry-grid_column">
            {blogList.map(item => {
              return (
                <Fade
                  key={item.id}
                  triggerOnce>
                  <div className="blog__card">
                    <h2>{item.content.title}</h2>
                    <Image
                      src={item.content.thumbnail}
                      alt={item.content.thumbnailAlt}
                      height={273}
                      width={487}
                    />
                    <p>{item.content.description}</p>
                    <InternalLink
                      href={`/blog/${item.urlKey}`}
                      size={Size.Medium}
                      hierarchy={ButtonHierarchy.Filled}
                      hasPadding
                      hasBackground>
                      Continue Reading
                    </InternalLink>
                  </div>
                </Fade>
              )
            })}
          </Masonry>
        )}
        {status === constants.componentStatus.ERROR && (
          <div
            className="alert alert-warning my-3"
            role="alert">
            There has been an issue fetching the FAQs. Please try again later.
          </div>
        )}
        <Pagination
          resultTotal={totalCount}
          pageSize={12}
          currentPage={Number(currentPage)}
          gotoPage={onClickMore}
        />
      </PageContainer>
    </>
  )
}

export default BlogPage

export const getServerSideProps = async () => {
  try {
    const articles = await getPageContentListService({
      type: 'BLOG',
      page: 1,
      pageSize: 12,
    })

    return {
      props: {
        articles,
      },
    }
  } catch (err) {
    return {
      notFound: true,
    }
  }
}
